import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { API_BASE_URL } from "@/config";
import { ApiError, apiErrorFromStatus, networkError } from "./errors";
import {
  getAccessToken,
  hasValidAccessToken,
  refreshAccessToken,
} from "./token";

/**
 * Typed REST client generated from docs/swagger.yaml.
 *
 * Behaviour (PRD §10):
 *  - Attaches `Authorization: Bearer <token>` for protected endpoints when a
 *    token is present; credentials are always included so the refresh cookie
 *    travels on auth endpoints.
 *  - On 401, performs a single de-duplicated refresh and retries the original
 *    request at most once.
 *  - Normalises Google RPC `Status` error bodies into ApiError.
 */

const RETRY_MARKER = "x-memos-retried";
const replayableRequests = new WeakMap<Request, Request>();

/** Paths that must never trigger an automatic token refresh (avoid loops). */
const NO_REFRESH_PATHS = ["/api/v1/auth/refresh", "/api/v1/auth/signin"];

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    // Always carry cookies (refresh token) on same-/cross-origin requests.
    // Rebuild the request with credentials so the HttpOnly refresh cookie
    // travels on auth endpoints (Request.credentials is read-only).
    const token = getAccessToken();
    const headers = new Headers(request.headers);
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const authenticatedRequest = new Request(request, {
      credentials: "include",
      headers,
    });
    // fetch consumes request bodies. Preserve a clone before the first attempt
    // so POST/PATCH/PUT requests can be replayed after a token refresh.
    replayableRequests.set(authenticatedRequest, authenticatedRequest.clone());
    return authenticatedRequest;
  },

  async onResponse({ request, response }) {
    if (response.ok) return response;

    const url = new URL(request.url);
    const canRefresh = !NO_REFRESH_PATHS.some((p) => url.pathname.endsWith(p));
    const alreadyRetried = request.headers.has(RETRY_MARKER);

    // On 401: refresh once and retry the original request once.
    if (response.status === 401 && canRefresh && !alreadyRetried) {
      try {
        const token = await refreshAccessToken();
        if (token) {
          const replayable = replayableRequests.get(request);
          if (!replayable) return response;
          const headers = new Headers(replayable.headers);
          headers.set("Authorization", `Bearer ${token}`);
          headers.set(RETRY_MARKER, "1");
          const retryRes = await fetch(
            new Request(replayable, { headers, credentials: "include" }),
          );
          return retryRes;
        }
      } catch {
        // Refresh failed; return the original 401 for the caller to handle.
        return response;
      }
    }
    return response;
  },
};

export const rawClient = createClient<paths>({
  baseUrl: API_BASE_URL,
});

rawClient.use(authMiddleware);

/** Parse Retry-After header (seconds or HTTP date) into seconds. */
function parseRetryAfter(res: Response): number | undefined {
  const h = res.headers.get("Retry-After");
  if (!h) return undefined;
  const secs = Number(h);
  if (!Number.isNaN(secs)) return secs;
  const date = Date.parse(h);
  return Number.isNaN(date)
    ? undefined
    : Math.max(0, Math.round((date - Date.now()) / 1000));
}

/**
 * Convert a fetch failure / error value from openapi-fetch into ApiError.
 * `data`/`error` are the values returned in openapi-fetch's result object.
 */
export function toApiError(error: unknown, response?: Response): ApiError {
  if (error instanceof ApiError) return error;
  if (response) {
    const status = (error ?? {}) as {
      code?: number;
      message?: string;
      details?: unknown;
    };
    return apiErrorFromStatus(
      status,
      response.status,
      parseRetryAfter(response),
    );
  }
  return networkError(error);
}

/** Ensure we have (or attempt to restore) a valid access token before a call. */
export async function ensureAccessToken(): Promise<string | null> {
  if (hasValidAccessToken()) return getAccessToken();
  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}
